import { useEffect, useState, type FormEvent } from 'react';
import { Loader2, Send, X } from 'lucide-react';
import type { PurchaseOrder } from '../types/purchase/purchaseOrder';
import { sendPurchaseOrder } from '../utils/purchase/purchaseOrderApi';
import { getCompany } from '../utils/companyApi';
import { toast } from '../utils/toast';

interface PurchaseOrderSendModalProps {
  purchaseOrder: PurchaseOrder;
  isOpen: boolean;
  onClose: () => void;
  onSent: (purchaseOrder: PurchaseOrder) => void;
}

const parseCcInput = (value: string): string | string[] | undefined => {
  const emails = value
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);
  if (emails.length === 0) return undefined;
  if (emails.length === 1) return emails[0];
  return emails;
};

const PurchaseOrderSendModal = ({
  purchaseOrder,
  isOpen,
  onClose,
  onSent,
}: PurchaseOrderSendModalProps) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setTo(purchaseOrder.vendor_snapshot.email ?? '');
    setCc('');
    setMessage('');
    setSubject(`Purchase order ${purchaseOrder.po_number}`);
    setLoadingDefaults(true);

    void getCompany()
      .then((company) => {
        if (cancelled) return;
        setSubject(`Purchase order ${purchaseOrder.po_number} from ${company.name}`);
      })
      .catch(() => {
        /* keep number-only subject fallback */
      })
      .finally(() => {
        if (!cancelled) setLoadingDefaults(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, purchaseOrder]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedTo = to.trim();
    if (!trimmedTo) {
      toast.error('Enter a recipient email address.');
      return;
    }

    try {
      setSubmitting(true);
      const updated = await sendPurchaseOrder(purchaseOrder.id, {
        to: trimmedTo,
        cc: parseCcInput(cc),
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
      });
      toast.success(`Purchase order ${updated.po_number} sent.`);
      onSent(updated);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send purchase order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const busy = submitting || loadingDefaults;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white my-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-medium text-gray-900">
            Send Purchase Order — {purchaseOrder.po_number}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="po-send-to" className="block text-sm font-medium text-gray-700 mb-1">
              To <span className="text-red-500">*</span>
            </label>
            <input
              id="po-send-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              required
              disabled={busy}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 disabled:bg-gray-50"
              placeholder="orders@supplier.com"
            />
          </div>

          <div>
            <label htmlFor="po-send-cc" className="block text-sm font-medium text-gray-700 mb-1">
              CC
            </label>
            <input
              id="po-send-cc"
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              disabled={busy}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 disabled:bg-gray-50"
              placeholder="ops@example.com, finance@example.com"
            />
            <p className="mt-1 text-xs text-gray-500">Separate multiple addresses with commas.</p>
          </div>

          <div>
            <label htmlFor="po-send-subject" className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <input
              id="po-send-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={busy}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 disabled:bg-gray-50"
            />
          </div>

          <div>
            <label htmlFor="po-send-message" className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              id="po-send-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={busy}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 disabled:bg-gray-50 resize-y"
              placeholder="Optional note above the default email notice."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex items-center px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <Send className="h-5 w-5 mr-2" />
              )}
              {submitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseOrderSendModal;
